import Phaser from 'phaser';
import { GAME_WIDTH } from '../config';
import {
  GENERAL_BRANCHES,
  GENERAL_TALENT_GATES,
  GENERAL_TALENT_NODES,
  getGeneralTalentNode,
  type GeneralBranchId,
  type GeneralTalentNodeDef,
} from '../../data/generalTalents';
import {
  VANGUARD_BRANCHES,
  VANGUARD_TALENT_GATES,
  VANGUARD_TALENT_NODES,
  getVanguardTalentNode,
  type TalentNodeDef,
  type VanguardBranchId,
} from '../../data/vanguardTalents';
import {
  applyGeneralTalentPurchaseToPermanent,
  generalRanksOf,
  previewGeneralTalentPurchase,
  unspentGeneralPoints,
  visualStateForGeneralNode,
} from '../../domain/generalTalents';
import {
  applyTalentPurchaseUpToPermanent,
  availableClassTalentPoints,
  canPurchaseNodeTier,
  describeNextRankEffect,
  isSpecialisationOpen,
  pointsSpentInBranch,
  previewTalentPurchase,
  ranksOf,
  unlockedTiersForBranch,
  visualStateForNode,
} from '../../domain/talents';
import { getSkillDefinition } from '../../domain/skills';
import {
  CLASS_LEVEL_CAP,
  GENERAL_LEVEL_CAP,
  SAVE_SCHEMA_VERSION,
  createDefaultPermanentSave,
  type PermanentSave,
} from '../../persistence/schema';
import { createSaveStore } from '../../persistence/saveStore';
import { mountShell, paintShellBackdrop } from '../ui/shellUi';
import { drawOrnateRect, drawTalentNode } from '../ui/frames';
import { addUiButton, addUiPanel } from '../ui/kit';
import { addGameIcon, type IconId } from '../ui/icons';
import { addPixelText } from '../ui/pixelText';
import { UI } from '../ui/theme';

type TalentTab = 'general' | 'vanguard';

const BRANCH_ICONS: Record<VanguardBranchId, IconId> = {
  general: 'talent.vanguard',
  berserker: 'talent.berserker',
  skirmisher: 'talent.skirmisher',
  bulwark: 'talent.bulwark',
};

const GENERAL_BRANCH_ICONS: Record<GeneralBranchId, IconId> = {
  offence: 'equip.main_hand',
  defence: 'stat.shield',
  dice: 'stat.dice',
};

const NODE_RADIUS = 22;
const MIN_ZOOM = 0.45;
const MAX_ZOOM = 1.35;
const TREE_WIDTH = 1280;
const TREE_HEIGHT = 420;

/**
 * Talents shell — shared General tree + Vanguard class tree.
 */
export class TalentsScene extends Phaser.Scene {
  private tab: TalentTab = 'general';
  private treeHost?: Phaser.GameObjects.Container;
  private panZoomRoot?: Phaser.GameObjects.Container;
  private detailHost?: Phaser.GameObjects.Container;
  private headerHost?: Phaser.GameObjects.Container;
  private tabButtons: Phaser.GameObjects.Container[] = [];
  private tabIcons: Phaser.GameObjects.Image[] = [];
  private tabY = 0;
  private contentTop = 0;
  private contentHeight = 0;
  private selectedNodeId: string | null = null;
  private permanent: PermanentSave = createDefaultPermanentSave();
  private panX = 40;
  private panY = 40;
  private zoom = 0.72;
  private dragPointerId: number | null = null;
  private dragLastX = 0;
  private dragLastY = 0;
  private pinchStartDist = 0;
  private pinchStartZoom = 1;
  private treeMask?: Phaser.Display.Masks.GeometryMask;
  private statusMessage = '';

  constructor() {
    super('Talents');
  }

  create(): void {
    paintShellBackdrop(this, 0x101428);
    const bounds = mountShell(this, 'talents');
    this.reloadPermanent();
    this.contentTop = bounds.top + 100;
    this.contentHeight = bounds.height - 110;
    this.tabY = bounds.top + 52;

    this.headerHost = this.add.container(0, 0);
    this.renderHeader();
    this.renderTabs();

    this.treeHost = this.add.container(0, this.contentTop);
    this.treeHost.add(
      addUiPanel(this, 12, 0, GAME_WIDTH - 24, this.contentHeight, 'stoneDark'),
    );

    const maskG = this.make.graphics({ x: 0, y: 0 });
    maskG.fillStyle(0xffffff);
    maskG.fillRect(16, this.contentTop + 8, GAME_WIDTH - 32, this.contentHeight - 16);
    this.treeMask = maskG.createGeometryMask();

    this.detailHost = this.add.container(0, 0).setDepth(20);

    this.renderTree();
    this.bindPanZoom();
  }

  private reloadPermanent(): void {
    this.permanent =
      createSaveStore().load()?.permanent ?? createDefaultPermanentSave();
  }

  private vanguardClass() {
    return this.permanent.classes.vanguard;
  }

  private unspentClass(): number {
    return availableClassTalentPoints(this.vanguardClass());
  }

  private unspentGeneral(): number {
    return unspentGeneralPoints(this.permanent.general);
  }

  private persistPermanent(next: PermanentSave): void {
    const store = createSaveStore();
    const previous = store.load();
    store.save({
      version: SAVE_SCHEMA_VERSION,
      savedAt: Date.now(),
      rngState: previous?.rngState ?? 0,
      permanent: next,
      run: previous?.run ?? null,
    });
    this.permanent = next;
  }

  private renderHeader(): void {
    if (!this.headerHost) return;
    this.headerHost.removeAll(true);
    const cls = this.vanguardClass();
    const gen = this.permanent.general;
    const cx = GAME_WIDTH / 2;
    const boundsTop = this.tabY - 52;

    this.headerHost.add(
      addGameIcon(this, 'talent.general', cx - 170, boundsTop + 12, {
        size: 14,
        color: this.tab === 'general' ? UI.goldBright : UI.ironLight,
      }),
    );
    this.headerHost.add(
      addPixelText(
        this,
        cx,
        boundsTop + 6,
        `General Lv ${gen.level}/${GENERAL_LEVEL_CAP} · ${this.unspentGeneral()} unspent · ${gen.xp} XP`,
        {
          font: this.tab === 'general' ? 'ui-yellow' : 'ui-grey',
          size: 10,
          originX: 0.5,
        },
      ),
    );
    this.headerHost.add(
      addGameIcon(this, 'talent.vanguard', cx - 170, boundsTop + 30, {
        size: 14,
        color: this.tab === 'vanguard' ? UI.goldBright : UI.ironLight,
      }),
    );
    this.headerHost.add(
      addGameIcon(this, 'talent.points', cx + 158, boundsTop + 30, {
        size: 14,
        color: UI.cyan,
      }),
    );
    this.headerHost.add(
      addPixelText(
        this,
        cx,
        boundsTop + 24,
        `Vanguard Lv ${cls.level}/${CLASS_LEVEL_CAP} · ${this.unspentClass()} unspent`,
        {
          font: this.tab === 'vanguard' ? 'ui-yellow' : 'ui-grey',
          size: 10,
          originX: 0.5,
        },
      ),
    );
  }

  private renderTabs(): void {
    const defs: { id: TalentTab; label: string; icon: IconId }[] = [
      { id: 'general', label: 'GENERAL', icon: 'talent.general' },
      { id: 'vanguard', label: 'VANGUARD', icon: 'talent.vanguard' },
    ];
    const w = 152;
    const gap = 12;
    const total = defs.length * w + (defs.length - 1) * gap;
    const startX = GAME_WIDTH / 2 - total / 2;
    const y = this.tabY;

    for (const btn of this.tabButtons) btn.destroy();
    for (const icon of this.tabIcons) icon.destroy();
    this.tabButtons = [];
    this.tabIcons = [];

    defs.forEach((def, i) => {
      const x = startX + i * (w + gap) + w / 2;
      const active = def.id === this.tab;
      const btn = addUiButton(
        this,
        x,
        y + 18,
        def.label,
        () => {
          if (this.tab === def.id) return;
          this.tab = def.id;
          this.selectedNodeId = null;
          this.statusMessage = '';
          this.renderTabs();
          this.renderTree();
          this.renderDetail();
        },
        {
          width: w,
          height: 36,
          state: active ? 'selected' : 'normal',
          fontSize: 14,
        },
      );
      this.tabButtons.push(btn);
      const icon = addGameIcon(this, def.icon, x - 54, y + 18, {
        size: 16,
        color: active ? UI.goldBright : UI.ironLight,
      });
      this.tabIcons.push(icon);
    });
  }

  private renderTree(): void {
    if (this.panZoomRoot) {
      this.panZoomRoot.destroy(true);
      this.panZoomRoot = undefined;
    }
    this.panZoomRoot = this.add.container(this.panX, this.panY);
    this.panZoomRoot.setScale(this.zoom);
    if (this.treeMask) {
      this.panZoomRoot.setMask(this.treeMask);
    }
    this.treeHost?.add(this.panZoomRoot);

    if (this.tab === 'general') {
      this.drawGeneralTree(this.panZoomRoot);
      this.renderDetail();
      return;
    }

    this.drawVanguardTree(this.panZoomRoot);
    this.renderDetail();
  }

  private drawGeneralTree(root: Phaser.GameObjects.Container): void {
    const ranks = this.permanent.general.talentRanks;
    const g = this.add.graphics();
    root.add(g);

    for (const node of GENERAL_TALENT_NODES) {
      for (const parentId of node.decorativeParents ?? []) {
        const parent = getGeneralTalentNode(parentId);
        g.lineStyle(2, 0x3a4560, 0.85);
        g.lineBetween(
          parent.layout.x,
          parent.layout.y,
          node.layout.x,
          node.layout.y,
        );
      }
    }

    for (const branch of GENERAL_BRANCHES) {
      const nodes = GENERAL_TALENT_NODES.filter((n) => n.branch === branch.id);
      const anchor = nodes[0];
      if (!anchor) continue;
      root.add(
        addGameIcon(
          this,
          GENERAL_BRANCH_ICONS[branch.id],
          anchor.layout.x,
          anchor.layout.y - 48,
          { size: 18, color: UI.goldBright },
        ),
      );
      root.add(
        addPixelText(
          this,
          anchor.layout.x,
          anchor.layout.y - 68,
          branch.name,
          { font: 'ui-yellow', size: 12, originX: 0.5 },
        ),
      );
    }

    root.add(
      addPixelText(this, 420, 8, `Tree capacity ${GENERAL_TALENT_GATES.treeTotalRanks} ranks · milestone pts reserved (schedule OPEN)`, {
        font: 'ui-grey',
        size: 9,
        originX: 0.5,
      }),
    );

    for (const node of GENERAL_TALENT_NODES) {
      this.drawGeneralNode(root, node, generalRanksOf(ranks, node.id));
    }
  }

  private drawGeneralNode(
    root: Phaser.GameObjects.Container,
    node: GeneralTalentNodeDef,
    current: number,
  ): void {
    const state = visualStateForGeneralNode(this.permanent.general, node.id);
    const frameState =
      state === 'maxed' || state === 'purchased'
        ? 'unlocked'
        : state === 'available'
          ? 'available'
          : 'locked';
    const g = this.add.graphics();
    root.add(g);
    drawTalentNode(g, node.layout.x, node.layout.y, NODE_RADIUS, frameState);
    if (this.selectedNodeId === node.id) {
      g.lineStyle(2, UI.goldBright, 1);
      g.strokeCircle(node.layout.x, node.layout.y, NODE_RADIUS + 4);
    }
    root.add(
      addPixelText(
        this,
        node.layout.x,
        node.layout.y - 2,
        `${current}/${node.maxRanks}`,
        { font: 'ui-yellow', size: 10, originX: 0.5, originY: 0.5 },
      ),
    );
    root.add(
      addPixelText(this, node.layout.x, node.layout.y + NODE_RADIUS + 10, node.name, {
        font: 'ui-grey',
        size: 9,
        originX: 0.5,
      }),
    );
    const hit = this.add
      .circle(node.layout.x, node.layout.y, NODE_RADIUS + 8, 0xffffff, 0.001)
      .setInteractive({ useHandCursor: true });
    root.add(hit);
    hit.on('pointerup', (pointer: Phaser.Input.Pointer) => {
      if (pointer.getDistance() > 18) return;
      this.selectedNodeId = node.id;
      this.statusMessage = '';
      this.renderTree();
    });
  }

  private drawVanguardTree(root: Phaser.GameObjects.Container): void {
    const cls = this.vanguardClass();
    const ranks = cls.talentRanks;
    const unspent = this.unspentClass();
    const g = this.add.graphics();
    root.add(g);

    // Branch headers
    for (const branch of VANGUARD_BRANCHES) {
      const nodes = VANGUARD_TALENT_NODES.filter((n) => n.branch === branch.id);
      if (nodes.length === 0) continue;
      const xs = nodes.map((n) => n.layout.x);
      const midX = (Math.min(...xs) + Math.max(...xs)) / 2;
      const open =
        branch.id === 'general' || isSpecialisationOpen(ranks);
      const spent = pointsSpentInBranch(ranks, branch.id);
      const tiers = unlockedTiersForBranch(ranks, branch.id);
      root.add(
        addGameIcon(this, BRANCH_ICONS[branch.id], midX - 48, 18, {
          size: 14,
          color: open ? UI.goldBright : UI.ironLight,
        }),
      );
      root.add(
        addPixelText(
          this,
          midX + 4,
          12,
          `${branch.name} · ${spent}pts · T${tiers.join('/') || '—'}`,
          {
            font: open ? 'ui-yellow' : 'ui-grey',
            size: 9,
            originX: 0.5,
            maxWidth: 200,
            align: 'center',
          },
        ),
      );
      if (!open) {
        root.add(
          addPixelText(
            this,
            midX,
            28,
            `Needs ${VANGUARD_TALENT_GATES.specialisationsAtGeneralPoints} General`,
            { font: 'ui-grey', size: 8, originX: 0.5 },
          ),
        );
      }
    }

    // Decorative connectors (never purchase prerequisites)
    g.lineStyle(2, UI.ironLight, 0.55);
    for (const node of VANGUARD_TALENT_NODES) {
      for (const parentId of node.decorativeParents ?? []) {
        const parent = getVanguardTalentNode(parentId);
        g.lineBetween(
          parent.layout.x,
          parent.layout.y,
          node.layout.x,
          node.layout.y,
        );
      }
    }

    for (const node of VANGUARD_TALENT_NODES) {
      this.drawNode(root, g, node, ranks, unspent);
    }

    root.add(
      addPixelText(
        this,
        TREE_WIDTH / 2,
        TREE_HEIGHT - 24,
        'Drag to pan · pinch to zoom · lines decorative only · combat effects deferred',
        {
          font: 'ui-grey',
          size: 8,
          originX: 0.5,
          maxWidth: TREE_WIDTH - 40,
          align: 'center',
        },
      ),
    );
  }

  private drawNode(
    root: Phaser.GameObjects.Container,
    g: Phaser.GameObjects.Graphics,
    node: TalentNodeDef,
    ranks: Record<string, number>,
    unspent: number,
  ): void {
    const current = ranksOf(ranks, node.id);
    const visual = visualStateForNode(ranks, node, unspent);
    const selected = this.selectedNodeId === node.id;
    const frameState =
      visual === 'locked'
        ? 'locked'
        : visual === 'available'
          ? 'available'
          : 'unlocked';
    drawTalentNode(g, node.layout.x, node.layout.y, NODE_RADIUS, frameState);
    if (selected) {
      g.lineStyle(3, UI.goldBright, 1);
      g.strokeCircle(node.layout.x, node.layout.y, NODE_RADIUS + 4);
    }

    const stateIcon: IconId =
      visual === 'locked'
        ? 'talent.locked'
        : visual === 'available'
          ? 'talent.available'
          : visual === 'maxed'
            ? 'talent.unlocked'
            : selected
              ? 'talent.selected'
              : 'talent.unlocked';
    root.add(
      addGameIcon(this, stateIcon, node.layout.x + 16, node.layout.y - 16, {
        size: 12,
        color:
          visual === 'locked'
            ? UI.ironLight
            : visual === 'available'
              ? UI.goldBright
              : UI.cyan,
      }),
    );

    const label =
      node.kind === 'active'
        ? current > 0
          ? 'ON'
          : 'SK'
        : `${current}/${node.maxRanks}`;
    root.add(
      addPixelText(this, node.layout.x, node.layout.y - 6, label, {
        font: visual === 'locked' ? 'ui-grey' : 'ui-yellow',
        size: 9,
        originX: 0.5,
      }),
    );
    root.add(
      addPixelText(this, node.layout.x, node.layout.y + 28, node.name, {
        font: 'ui-grey',
        size: 7,
        originX: 0.5,
        maxWidth: 72,
        align: 'center',
      }),
    );

    if (visual === 'locked' && !canPurchaseNodeTier(ranks, node)) {
      root.add(
        addGameIcon(this, 'ui.locked', node.layout.x, node.layout.y, {
          size: 14,
          color: UI.ironLight,
          alpha: 0.85,
        }),
      );
    }

    const hit = this.add
      .circle(node.layout.x, node.layout.y, NODE_RADIUS + 10, 0xffffff, 0.001)
      .setInteractive({ useHandCursor: true });
    root.add(hit);
    hit.on('pointerup', (pointer: Phaser.Input.Pointer) => {
      if (pointer.getDistance() > 18) return;
      this.selectedNodeId = node.id;
      this.statusMessage = '';
      this.renderTree();
    });
  }

  private renderDetail(): void {
    if (!this.detailHost) return;
    this.detailHost.removeAll(true);
    if (!this.selectedNodeId) {
      return;
    }
    if (this.tab === 'general') {
      this.renderGeneralDetail(this.selectedNodeId);
      return;
    }
    if (this.tab !== 'vanguard') {
      return;
    }
    this.renderVanguardDetail(this.selectedNodeId);
  }

  private renderGeneralDetail(nodeId: string): void {
    if (!this.detailHost) return;
    let node: GeneralTalentNodeDef;
    try {
      node = getGeneralTalentNode(nodeId);
    } catch {
      return;
    }
    const gen = this.permanent.general;
    const current = generalRanksOf(gen.talentRanks, node.id);
    const unspent = this.unspentGeneral();
    const preview1 = previewGeneralTalentPurchase(gen, node.id, 1);
    const room = Math.max(0, node.maxRanks - current);
    const multi = Math.min(room, unspent);
    const panelW = GAME_WIDTH - 28;
    const panelH = 210;
    const panelX = 14;
    const panelY = this.contentTop + this.contentHeight - panelH - 8;

    const g = this.add.graphics();
    drawOrnateRect(g, panelX, panelY, panelW, panelH, {
      fill: UI.bgPanel,
      active: true,
    });
    this.detailHost.add(g);
    this.detailHost.add(
      addGameIcon(
        this,
        GENERAL_BRANCH_ICONS[node.branch],
        panelX + 18,
        panelY + 18,
        { size: 16, color: UI.goldBright },
      ),
    );
    this.detailHost.add(
      addPixelText(this, panelX + 34, panelY + 10, node.name, {
        font: 'ui-yellow',
        size: 14,
      }),
    );
    this.detailHost.add(
      addPixelText(
        this,
        panelX + panelW - 12,
        panelY + 12,
        GENERAL_BRANCHES.find((b) => b.id === node.branch)?.name ?? node.branch,
        { font: 'ui-grey', size: 10, originX: 1 },
      ),
    );
    this.detailHost.add(
      addPixelText(
        this,
        panelX + 16,
        panelY + 36,
        `Ranks ${current}/${node.maxRanks} · ${unspent} unspent`,
        { font: 'ui-yellow', size: 11 },
      ),
    );
    this.detailHost.add(
      addPixelText(this, panelX + 16, panelY + 56, node.effect.description, {
        font: 'ui-brown',
        size: 10,
        maxWidth: panelW - 32,
      }),
    );
    if (node.effect.openNote) {
      this.detailHost.add(
        addPixelText(
          this,
          panelX + 16,
          panelY + 88,
          `TBD: ${node.effect.openNote}`,
          {
            font: 'ui-grey',
            size: 9,
            maxWidth: panelW - 32,
          },
        ),
      );
    }

    const lockReason = preview1.rejectReason;
    let status = this.statusMessage;
    if (!status && lockReason && lockReason !== 'already_maxed') {
      status = this.rejectLabel(lockReason);
    } else if (!status && current >= node.maxRanks) {
      status = 'Max rank';
    }
    if (status) {
      this.detailHost.add(
        addPixelText(this, panelX + 16, panelY + 112, status, {
          font: 'ui-grey',
          size: 10,
          maxWidth: panelW - 32,
        }),
      );
    }

    const btnY = panelY + panelH - 36;
    this.detailHost.add(
      addUiButton(
        this,
        panelX + 70,
        btnY,
        'CLOSE',
        () => {
          this.selectedNodeId = null;
          this.statusMessage = '';
          this.renderTree();
        },
        { width: 100, height: 34, state: 'alt', fontSize: 12 },
      ),
    );
    const canBuyOne = preview1.canPurchase;
    this.detailHost.add(
      addUiButton(
        this,
        panelX + panelW / 2,
        btnY,
        canBuyOne ? 'BUY +1' : 'LOCKED',
        () => this.tryPurchaseGeneral(node.id, 1),
        {
          width: 110,
          height: 34,
          state: canBuyOne ? 'normal' : 'disabled',
          fontSize: 12,
        },
      ),
    );
    const canMulti =
      multi > 1 &&
      previewGeneralTalentPurchase(gen, node.id, multi).canPurchase;
    this.detailHost.add(
      addUiButton(
        this,
        panelX + panelW - 70,
        btnY,
        canMulti ? `BUY +${multi}` : `MAX +${Math.max(multi, 1)}`,
        () => {
          if (multi <= 0) {
            this.statusMessage = this.rejectLabel(
              preview1.rejectReason ?? 'insufficient_points',
            );
            this.renderDetail();
            return;
          }
          this.tryPurchaseGeneral(node.id, multi);
        },
        {
          width: 110,
          height: 34,
          state: canMulti || (multi === 1 && canBuyOne) ? 'selected' : 'disabled',
          fontSize: 12,
        },
      ),
    );
  }

  private renderVanguardDetail(nodeId: string): void {
    if (!this.detailHost) return;
    let node: TalentNodeDef;
    try {
      node = getVanguardTalentNode(nodeId);
    } catch {
      return;
    }

    const cls = this.vanguardClass();
    const current = ranksOf(cls.talentRanks, node.id);
    const unspent = this.unspentClass();
    const preview1 = previewTalentPurchase(cls, node.id, 1);
    const room = Math.max(0, node.maxRanks - current);
    const multi = Math.min(room, unspent);
    const panelW = GAME_WIDTH - 28;
    const panelH = 210;
    const panelX = 14;
    const panelY = this.contentTop + this.contentHeight - panelH - 8;

    const g = this.add.graphics();
    drawOrnateRect(g, panelX, panelY, panelW, panelH, {
      fill: UI.bgPanel,
      active: true,
    });
    this.detailHost.add(g);

    this.detailHost.add(
      addGameIcon(this, BRANCH_ICONS[node.branch], panelX + 18, panelY + 18, {
        size: 16,
        color: UI.goldBright,
      }),
    );
    this.detailHost.add(
      addPixelText(this, panelX + 34, panelY + 10, node.name, {
        font: 'ui-yellow',
        size: 14,
      }),
    );
    this.detailHost.add(
      addPixelText(
        this,
        panelX + panelW - 12,
        panelY + 12,
        `T${node.tier} · ${node.kind}`,
        { font: 'ui-grey', size: 10, originX: 1 },
      ),
    );
    this.detailHost.add(
      addPixelText(
        this,
        panelX + 16,
        panelY + 36,
        `Ranks ${current}/${node.maxRanks} · ${unspent} unspent`,
        { font: 'ui-yellow', size: 11 },
      ),
    );
    let effectLine = describeNextRankEffect(node, current);
    if (node.kind === 'active' && node.effect.skillId) {
      try {
        const skill = getSkillDefinition(node.effect.skillId);
        effectLine = `${effectLine}. ${skill.description}`;
      } catch {
        // Skill def missing — keep unlock stub only.
      }
    }
    this.detailHost.add(
      addPixelText(this, panelX + 16, panelY + 56, effectLine, {
        font: 'ui-brown',
        size: 10,
        maxWidth: panelW - 32,
      }),
    );
    if (node.effect.openNote) {
      this.detailHost.add(
        addPixelText(
          this,
          panelX + 16,
          panelY + 88,
          `TBD: ${node.effect.openNote}`,
          {
            font: 'ui-grey',
            size: 9,
            maxWidth: panelW - 32,
          },
        ),
      );
    }

    const lockReason = preview1.rejectReason;
    let status = this.statusMessage;
    if (!status && lockReason && lockReason !== 'already_maxed') {
      status = this.rejectLabel(lockReason);
    } else if (!status && current >= node.maxRanks) {
      status = 'Max rank';
    }
    if (status) {
      this.detailHost.add(
        addPixelText(this, panelX + 16, panelY + 112, status, {
          font: 'ui-grey',
          size: 10,
          maxWidth: panelW - 32,
        }),
      );
    }

    const btnY = panelY + panelH - 36;
    this.detailHost.add(
      addUiButton(
        this,
        panelX + 70,
        btnY,
        'CLOSE',
        () => {
          this.selectedNodeId = null;
          this.statusMessage = '';
          this.renderTree();
        },
        { width: 100, height: 34, state: 'alt', fontSize: 12 },
      ),
    );

    const canBuyOne = preview1.canPurchase;
    this.detailHost.add(
      addUiButton(
        this,
        panelX + panelW / 2,
        btnY,
        canBuyOne ? 'BUY +1' : 'LOCKED',
        () => this.tryPurchase(node.id, 1),
        {
          width: 110,
          height: 34,
          state: canBuyOne ? 'normal' : 'disabled',
          fontSize: 12,
        },
      ),
    );

    const canMulti =
      multi > 1 && previewTalentPurchase(cls, node.id, multi).canPurchase;
    this.detailHost.add(
      addUiButton(
        this,
        panelX + panelW - 70,
        btnY,
        canMulti ? `BUY +${multi}` : `MAX +${Math.max(multi, 1)}`,
        () => {
          if (multi <= 0) {
            this.statusMessage = this.rejectLabel(
              preview1.rejectReason ?? 'insufficient_points',
            );
            this.renderDetail();
            return;
          }
          this.tryPurchase(node.id, multi);
        },
        {
          width: 110,
          height: 34,
          state: canMulti || (multi === 1 && canBuyOne) ? 'selected' : 'disabled',
          fontSize: 12,
        },
      ),
    );
  }

  private rejectLabel(reason: string): string {
    switch (reason) {
      case 'tier_locked':
        return 'Tier locked — spend more points in this branch';
      case 'branch_locked':
        return `Specialisation locked — need ${VANGUARD_TALENT_GATES.specialisationsAtGeneralPoints} General points`;
      case 'insufficient_points':
        return 'Not enough unspent talent points';
      case 'already_maxed':
        return 'Already at max rank';
      case 'invalid_ranks':
        return 'Cannot buy that many ranks';
      default:
        return 'Cannot purchase';
    }
  }

  private tryPurchase(nodeId: string, ranks: number): void {
    try {
      const next = applyTalentPurchaseUpToPermanent(
        this.permanent,
        'vanguard',
        nodeId,
        ranks,
      );
      const before = this.vanguardClass().spentPoints;
      const spent = next.classes.vanguard.spentPoints - before;
      this.persistPermanent(next);
      this.statusMessage =
        spent > 0 ? `Purchased ${spent} rank${spent === 1 ? '' : 's'}` : 'No ranks purchased';
      this.renderHeader();
      this.renderTree();
    } catch (err) {
      const message = err instanceof Error ? err.message : 'Purchase failed';
      this.statusMessage = message;
      this.renderDetail();
    }
  }

  private tryPurchaseGeneral(nodeId: string, ranks: number): void {
    try {
      let next = this.permanent;
      const before = next.general.spentPoints;
      for (let i = 0; i < ranks; i += 1) {
        next = applyGeneralTalentPurchaseToPermanent(next, nodeId, 1);
      }
      const spent = next.general.spentPoints - before;
      this.persistPermanent(next);
      this.statusMessage =
        spent > 0
          ? `Purchased ${spent} rank${spent === 1 ? '' : 's'}`
          : 'No ranks purchased';
      this.renderHeader();
      this.renderTree();
    } catch (err) {
      const message = err instanceof Error ? err.message : 'Purchase failed';
      this.statusMessage = message;
      this.renderDetail();
    }
  }

  private bindPanZoom(): void {
    // Scene-level pan/zoom — do NOT cover the tree with an interactive rect
    // (that would steal taps from talent nodes).
    this.input.addPointer(2);
    this.detailHost?.setDepth(20);

    this.input.on('pointerdown', (pointer: Phaser.Input.Pointer) => {
      if (this.tab !== 'vanguard' && this.tab !== 'general') return;
      // Ignore drags that begin on the detail panel.
      if (
        this.selectedNodeId &&
        pointer.y > this.contentTop + this.contentHeight - 220
      ) {
        return;
      }
      const pointers = this.input.manager.pointers.filter((p) => p.isDown);
      if (pointers.length >= 2) {
        const [a, b] = pointers;
        this.pinchStartDist = Phaser.Math.Distance.Between(
          a!.x,
          a!.y,
          b!.x,
          b!.y,
        );
        this.pinchStartZoom = this.zoom;
        this.dragPointerId = null;
        return;
      }
      this.dragPointerId = pointer.id;
      this.dragLastX = pointer.x;
      this.dragLastY = pointer.y;
    });

    this.input.on('pointermove', (pointer: Phaser.Input.Pointer) => {
      if (this.tab !== 'vanguard' && this.tab !== 'general') return;
      const pointers = this.input.manager.pointers.filter((p) => p.isDown);
      if (pointers.length >= 2) {
        const [a, b] = pointers;
        const dist = Phaser.Math.Distance.Between(a!.x, a!.y, b!.x, b!.y);
        if (this.pinchStartDist > 0) {
          this.zoom = Phaser.Math.Clamp(
            this.pinchStartZoom * (dist / this.pinchStartDist),
            MIN_ZOOM,
            MAX_ZOOM,
          );
          this.panZoomRoot?.setScale(this.zoom);
        }
        return;
      }
      if (this.dragPointerId !== pointer.id || !pointer.isDown) return;
      const dx = pointer.x - this.dragLastX;
      const dy = pointer.y - this.dragLastY;
      // Ignore tiny jitter so taps still register on nodes.
      if (Math.abs(dx) < 2 && Math.abs(dy) < 2) return;
      this.dragLastX = pointer.x;
      this.dragLastY = pointer.y;
      this.panX = Phaser.Math.Clamp(
        this.panX + dx,
        -TREE_WIDTH * this.zoom + 80,
        80,
      );
      this.panY = Phaser.Math.Clamp(
        this.panY + dy,
        -TREE_HEIGHT * this.zoom + 80,
        80,
      );
      this.panZoomRoot?.setPosition(this.panX, this.panY);
    });

    this.input.on('pointerup', () => {
      this.dragPointerId = null;
      this.pinchStartDist = 0;
    });

    this.input.on(
      'wheel',
      (
        _pointer: Phaser.Input.Pointer,
        _over: Phaser.GameObjects.GameObject[],
        _dx: number,
        dy: number,
      ) => {
        if (this.tab !== 'vanguard' && this.tab !== 'general') return;
        const factor = dy > 0 ? 0.92 : 1.08;
        this.zoom = Phaser.Math.Clamp(this.zoom * factor, MIN_ZOOM, MAX_ZOOM);
        this.panZoomRoot?.setScale(this.zoom);
      },
    );
  }
}
